import {
  Card,
  Row,
  Col,
  Select,
  Button,
  Space,
  Typography,
  Upload,
  Switch,
  Tag,
  Spin,
  Divider,
  Empty,
} from "antd";
import {
  UploadOutlined,
  EditOutlined,
  EyeOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  FaThermometerHalf,
  FaBolt,
  FaTint,
  FaMicrochip,
  FaWind,
} from "react-icons/fa";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useUnifilar from "./hooks/useUnifilar";
import { DiagramCanvas } from "./components/DiagramCanvas";
import "./unifilar.css";

const { Title, Text } = Typography;

// Derive status from pin + metrics
function getPinStatus(pin, metrics) {
  if (!pin.isActive) return "inactive";
  if (metrics?.some((m) => m.status === "FAIL")) return "alert";
  return "active";
}

// Status palette — industrial indicator lamps
const STATUS = {
  active:   { color: "#16A34A", label: "Operativo", dotBg: "#DCFCE7" },
  alert:    { color: "#DC2626", label: "Alerta",     dotBg: "#FEE2E2" },
  inactive: { color: "#9CA3AF", label: "Inactivo",   dotBg: "#F3F4F6" },
};

function getSensorIcon(name) {
  const n = (name || "").toLowerCase();
  if (n.includes("temp")) return <FaThermometerHalf />;
  if (n.includes("energ") || n.includes("power") || n.includes("watt")) return <FaBolt />;
  if (n.includes("flow") || n.includes("agua") || n.includes("water")) return <FaTint />;
  if (n.includes("aire") || n.includes("air") || n.includes("co2") || n.includes("hum")) return <FaWind />;
  return <FaMicrochip />;
}

// Mini stat block used in sidebar summary
function StatMini({ value, label, color }) {
  return (
    <div className="stat-mini">
      <Text
        strong
        style={{ fontSize: 22, lineHeight: 1, color, fontVariantNumeric: "tabular-nums" }}
      >
        {value}
      </Text>
      <Text type="secondary" style={{ fontSize: 11, marginTop: 2 }}>
        {label}
      </Text>
    </div>
  );
}

export default function Unifilar() {
  const { t } = useTranslation("global");
  const [isEditMode, setIsEditMode] = useState(false);

  const {
    stores,
    devices,
    selectedStore,
    setSelectedStore,
    pins,
    diagramUrl,
    deviceMetrics,
    loading,
    addPin,
    removePin,
    updatePinPosition,
    saveDiagram,
    removeDiagram,
  } = useUnifilar();

  // Enrich pins with computed status (derived during render — no extra effects)
  const enrichedPins = useMemo(
    () =>
      pins.map((pin) => ({
        ...pin,
        computedStatus: getPinStatus(pin, deviceMetrics[pin.devEui]),
      })),
    [pins, deviceMetrics],
  );

  const activeCount   = enrichedPins.filter((p) => p.computedStatus === "active").length;
  const alertCount    = enrichedPins.filter((p) => p.computedStatus === "alert").length;
  const inactiveCount = enrichedPins.filter((p) => p.computedStatus === "inactive").length;

  const handleDiagramUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => saveDiagram(e.target.result);
    reader.readAsDataURL(file);
    return false; // prevent auto-upload
  };

  const handleSelectStore = (val) => {
    const store = stores.find((s) => s.uuid === val) || null;
    setSelectedStore(store);
    setIsEditMode(false);
  };

  return (
    <div className="layout-content">
      <Row gutter={[20, 16]}>
        {/* ── Sidebar ── */}
        <Col xs={24} md={7} lg={6}>
          <Card
            bodyStyle={{ padding: "18px 16px" }}
            style={{
              borderRadius: 10,
              border: "1px solid rgba(0,0,0,0.08)",
              boxShadow: "none",
            }}
          >
            <Space direction="vertical" style={{ width: "100%" }} size={14}>
              {/* Title */}
              <Title level={5} style={{ margin: 0, fontSize: 14, letterSpacing: 0.3 }}>
                {t("unifilar.title")}
              </Title>

              {/* Store selector */}
              <div>
                <Text
                  type="secondary"
                  style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6 }}
                >
                  {t("unifilar.selectStore")}
                </Text>
                <Select
                  placeholder={t("unifilar.storePlaceholder")}
                  style={{ width: "100%", marginTop: 6 }}
                  value={selectedStore?.uuid ?? undefined}
                  onChange={handleSelectStore}
                  options={stores.map((s) => ({ value: s.uuid, label: s.name }))}
                  showSearch
                  allowClear
                  filterOption={(input, opt) =>
                    (opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  loading={stores.length === 0}
                />
              </div>

              {selectedStore && (
                <>
                  <Divider style={{ margin: "2px 0" }} />

                  {/* Status summary */}
                  {enrichedPins.length > 0 && (
                    <>
                      <div
                        style={{
                          display: "flex",
                          background: "#FAFAF9",
                          borderRadius: 8,
                          border: "1px solid rgba(0,0,0,0.07)",
                          overflow: "hidden",
                        }}
                      >
                        <StatMini value={activeCount}   label="operativos" color={STATUS.active.color}   />
                        <div style={{ width: 1, background: "rgba(0,0,0,0.07)" }} />
                        <StatMini value={alertCount}    label="alertas"     color={STATUS.alert.color}    />
                        <div style={{ width: 1, background: "rgba(0,0,0,0.07)" }} />
                        <StatMini value={inactiveCount} label="inactivos"   color={STATUS.inactive.color} />
                      </div>
                      <Divider style={{ margin: "2px 0" }} />
                    </>
                  )}

                  {/* Edit mode toggle */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Text style={{ fontSize: 13 }}>{t("unifilar.editMode")}</Text>
                    <Switch
                      checked={isEditMode}
                      onChange={setIsEditMode}
                      checkedChildren={<EditOutlined />}
                      unCheckedChildren={<EyeOutlined />}
                    />
                  </div>

                  {/* Edit controls */}
                  {isEditMode && (
                    <Space direction="vertical" style={{ width: "100%" }} size={6}>
                      <Upload
                        accept="image/*"
                        showUploadList={false}
                        beforeUpload={handleDiagramUpload}
                      >
                        <Button icon={<UploadOutlined />} block size="small">
                          {t("unifilar.uploadDiagram")}
                        </Button>
                      </Upload>
                      {diagramUrl && (
                        <Button
                          danger
                          ghost
                          icon={<DeleteOutlined />}
                          onClick={removeDiagram}
                          block
                          size="small"
                        >
                          {t("unifilar.removeDiagram")}
                        </Button>
                      )}
                    </Space>
                  )}

                  <Divider style={{ margin: "2px 0" }} />

                  {/* Pinned sensors list */}
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <Text
                        type="secondary"
                        style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6 }}
                      >
                        {t("unifilar.pinnedSensors")}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {enrichedPins.length}
                      </Text>
                    </div>

                    <Spin spinning={loading} size="small">
                      <Space direction="vertical" style={{ width: "100%" }} size={4}>
                        {enrichedPins.length === 0 ? (
                          <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.5 }}>
                            {t("unifilar.noPins")}
                          </Text>
                        ) : (
                          enrichedPins.map((pin) => {
                            const s = STATUS[pin.computedStatus];
                            return (
                              <div key={pin.id} className="device-pin-row">
                                {/* Status dot */}
                                <div
                                  className="status-dot"
                                  style={{ background: s.color }}
                                />
                                {/* Icon */}
                                <span style={{ color: s.color, fontSize: 13, flexShrink: 0 }}>
                                  {getSensorIcon(pin.deviceName)}
                                </span>
                                {/* Name */}
                                <Text
                                  style={{
                                    fontSize: 12,
                                    flex: 1,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                  title={pin.deviceName}
                                >
                                  {pin.deviceName}
                                </Text>
                              </div>
                            );
                          })
                        )}
                      </Space>
                    </Spin>
                  </div>
                </>
              )}

              {!selectedStore && (
                <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.6 }}>
                  {t("unifilar.selectStorePrompt")}
                </Text>
              )}
            </Space>
          </Card>
        </Col>

        {/* ── Canvas panel ── */}
        <Col xs={24} md={17} lg={18}>
          <Card
            bodyStyle={{ padding: 0 }}
            style={{
              borderRadius: 10,
              border: "1px solid rgba(0,0,0,0.08)",
              boxShadow: "none",
              overflow: "hidden",
            }}
          >
            {/* Card header */}
            <div
              style={{
                padding: "12px 18px",
                borderBottom: "1px solid rgba(0,0,0,0.07)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                minHeight: 48,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {selectedStore ? (
                  <>
                    <Text strong style={{ fontSize: 14 }}>
                      {selectedStore.name}
                    </Text>
                    {isEditMode && (
                      <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>
                        {t("unifilar.editModeTag")}
                      </Tag>
                    )}
                    {alertCount > 0 && (
                      <Tag color="error" style={{ margin: 0, fontSize: 11 }}>
                        {alertCount} {t("unifilar.alertsTag")}
                      </Tag>
                    )}
                  </>
                ) : (
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    {t("unifilar.noStoreSelected")}
                  </Text>
                )}
              </div>

              {selectedStore && enrichedPins.length > 0 && (
                <div style={{ display: "flex", gap: 6 }}>
                  {activeCount > 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <div className="status-dot" style={{ background: STATUS.active.color }} />
                      <Text style={{ fontSize: 11, color: STATUS.active.color }}>{activeCount}</Text>
                    </div>
                  )}
                  {alertCount > 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <div className="status-dot" style={{ background: STATUS.alert.color }} />
                      <Text style={{ fontSize: 11, color: STATUS.alert.color }}>{alertCount}</Text>
                    </div>
                  )}
                  {inactiveCount > 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <div className="status-dot" style={{ background: STATUS.inactive.color }} />
                      <Text style={{ fontSize: 11, color: STATUS.inactive.color }}>{inactiveCount}</Text>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Body */}
            {!selectedStore ? (
              <div style={{ padding: "80px 0", background: "#FAFAF9" }}>
                <Empty
                  description={
                    <Text type="secondary">{t("unifilar.selectStorePrompt")}</Text>
                  }
                />
              </div>
            ) : (
              <DiagramCanvas
                diagramUrl={diagramUrl}
                pins={pins}
                deviceMetrics={deviceMetrics}
                devices={devices}
                isEditMode={isEditMode}
                onAddPin={addPin}
                onRemovePin={removePin}
                onUpdatePinPosition={updatePinPosition}
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
